/**
 * GitHub言語から技術スタックを推定するマッピング
 */

// 主要言語から関連する技術スタックを推定
const languageToTechStack: Record<string, string[]> = {
  // JavaScript/TypeScript系
  'TypeScript': ['TypeScript'],
  'JavaScript': ['JavaScript'],

  // Python系
  'Python': ['Python'],

  // Java系
  'Java': ['Java'],
  'Kotlin': ['Kotlin'],

  // Go系
  'Go': ['Go'],

  // Rust系
  'Rust': ['Rust'],

  // Ruby系
  'Ruby': ['Ruby'],

  // PHP系
  'PHP': ['PHP'],

  // C系
  'C': ['C'],
  'C++': ['C++'],
  'C#': ['C#'],

  // Swift系
  'Swift': ['Swift'],

  // その他
  'Shell': ['Shell', 'Bash'],
  'HCL': ['Terraform', 'IaC'],
  'Dockerfile': ['Docker'],
  'Vue': ['Vue', 'JavaScript'],
  'HTML': ['HTML', 'CSS'],
  'CSS': ['CSS'],
  'SCSS': ['SCSS', 'CSS'],
  'Sass': ['Sass', 'CSS'],
}

// 言語の詳細比率から追加の技術スタックを推定
// 複数の言語が検出された場合のヒントを提供
const languageCombinationHints: Record<string, { requires: string[], suggests: string[] }> = {
  // TypeScript + HTML + CSS = フロントエンドフレームワーク
  'TypeScript+HTML': { requires: ['TypeScript', 'HTML'], suggests: ['React', 'Vue', 'Angular'] },
  'JavaScript+HTML': { requires: ['JavaScript', 'HTML'], suggests: ['React', 'Vue', 'JavaScript'] },

  // Python + HCL = インフラ系
  'Python+HCL': { requires: ['Python', 'HCL'], suggests: ['AWS Lambda', 'Terraform'] },

  // TypeScript + Dockerfile = コンテナ化アプリ
  'TypeScript+Dockerfile': { requires: ['TypeScript', 'Dockerfile'], suggests: ['Docker', 'Node.js'] },
}

/**
 * GitHub言語から技術スタックを推定
 * @param primaryLanguage リポジトリの主要言語
 * @param additionalLanguages 追加の言語（オプション）
 * @returns 推定された技術スタック
 */
export function inferTechStackFromLanguage(
  primaryLanguage: string | null,
  additionalLanguages?: string[]
): string[] {
  if (!primaryLanguage) return []

  const techStack: Set<string> = new Set()

  // 主要言語から基本の技術スタックを追加
  const baseTech = languageToTechStack[primaryLanguage]
  if (baseTech) {
    baseTech.forEach(tech => techStack.add(tech))
  } else {
    // マッピングがない場合は言語名をそのまま追加
    techStack.add(primaryLanguage)
  }

  // 追加の言語からも技術スタックを推定
  if (additionalLanguages) {
    for (const lang of additionalLanguages) {
      const tech = languageToTechStack[lang]
      if (tech) {
        tech.forEach(t => techStack.add(t))
      }
    }

    // 言語の組み合わせからヒントを適用
    for (const [, hint] of Object.entries(languageCombinationHints)) {
      const allLanguages = [primaryLanguage, ...additionalLanguages].map(l => l.toLowerCase())
      const requiredLangs = hint.requires.map(l => l.toLowerCase())

      if (requiredLangs.every(req => allLanguages.some(lang => lang.includes(req) || req.includes(lang)))) {
        // 最初の推定技術を追加（ユーザーが選択できるようにする）
        if (hint.suggests.length > 0) {
          techStack.add(hint.suggests[0])
        }
      }
    }
  }

  return Array.from(techStack)
}

/**
 * リポジトリ名からフレームワークを推定
 * @param repoName リポジトリ名
 * @returns 推定されたフレームワーク
 */
export function inferFrameworkFromRepoName(repoName: string): string[] {
  const frameworks: string[] = []
  const lowerName = repoName.toLowerCase()

  // フレームワーク名が含まれているか確認
  if (lowerName.includes('react')) frameworks.push('React')
  if (lowerName.includes('vue')) frameworks.push('Vue')
  if (lowerName.includes('angular')) frameworks.push('Angular')
  if (lowerName.includes('next')) frameworks.push('Next.js')
  if (lowerName.includes('nuxt')) frameworks.push('Nuxt')
  if (lowerName.includes('svelte')) frameworks.push('Svelte')
  if (lowerName.includes('django')) frameworks.push('Django')
  if (lowerName.includes('flask')) frameworks.push('Flask')
  if (lowerName.includes('fastapi')) frameworks.push('FastAPI')
  if (lowerName.includes('express')) frameworks.push('Express')
  if (lowerName.includes('nest')) frameworks.push('NestJS')
  if (lowerName.includes('spring')) frameworks.push('Spring')
  if (lowerName.includes('lambda')) frameworks.push('AWS Lambda')
  if (lowerName.includes('terraform') || lowerName.includes('infra')) frameworks.push('Terraform')
  if (lowerName.includes('docker')) frameworks.push('Docker')
  if (lowerName.includes('api')) frameworks.push('REST API')
  if (lowerName.includes('bot')) frameworks.push('Bot')

  return frameworks
}

/**
 * 言語とリポジトリ名から総合的に技術スタックを推定
 */
export function inferFullTechStack(
  primaryLanguage: string | null,
  repoName: string,
  additionalLanguages?: string[]
): string[] {
  const techStack = new Set<string>()

  // 言語から推定
  const langTech = inferTechStackFromLanguage(primaryLanguage, additionalLanguages)
  langTech.forEach(tech => techStack.add(tech))

  // リポジトリ名から推定
  const frameworkTech = inferFrameworkFromRepoName(repoName)
  frameworkTech.forEach(tech => techStack.add(tech))

  return Array.from(techStack)
}
